/**
 * Mirrors of the Prisma enums, expressed as plain const objects so that web,
 * native and Node clients can import them without pulling in @prisma/client.
 * Keep in sync with the Prisma schema. The test that enforces that lives in
 * the backend repo now (src/common/constants/enums.drift.spec.ts) -- after the
 * split, that is the only place both the schema and this package are in scope.
 */

export const TableState = {
  AVAILABLE: 'AVAILABLE',
  OCCUPIED: 'OCCUPIED',
  PAYMENT_PENDING: 'PAYMENT_PENDING',
  CLEANING: 'CLEANING',
  RESERVED: 'RESERVED',
  BLOCKED: 'BLOCKED',
  OUT_OF_SERVICE: 'OUT_OF_SERVICE',
} as const;
export type TableState = (typeof TableState)[keyof typeof TableState];

export const AreaType = {
  INDOOR: 'INDOOR',
  OUTDOOR: 'OUTDOOR',
  VIP: 'VIP',
  SMOKING: 'SMOKING',
  FAMILY: 'FAMILY',
  KIDS: 'KIDS',
  GAMING: 'GAMING',
  ROOFTOP: 'ROOFTOP',
  TERRACE: 'TERRACE',
  PRIVATE_ROOM: 'PRIVATE_ROOM',
  OTHER: 'OTHER',
} as const;
export type AreaType = (typeof AreaType)[keyof typeof AreaType];

export const MapElementType = {
  TABLE: 'TABLE',
  RESOURCE: 'RESOURCE',
  WALL: 'WALL',
  DOOR: 'DOOR',
  WINDOW: 'WINDOW',
  BAR: 'BAR',
  KITCHEN: 'KITCHEN',
  CASHIER: 'CASHIER',
  BATHROOM: 'BATHROOM',
  ENTRANCE: 'ENTRANCE',
  STAIRS: 'STAIRS',
  TEXT: 'TEXT',
  CUSTOM: 'CUSTOM',
} as const;
export type MapElementType = (typeof MapElementType)[keyof typeof MapElementType];

export const ResourceType = {
  PLAYSTATION: 'PLAYSTATION',
  BILLIARDS: 'BILLIARDS',
  KIDS_AREA: 'KIDS_AREA',
  KARAOKE_ROOM: 'KARAOKE_ROOM',
  PRIVATE_ROOM: 'PRIVATE_ROOM',
  MEETING_ROOM: 'MEETING_ROOM',
  OTHER: 'OTHER',
} as const;
export type ResourceType = (typeof ResourceType)[keyof typeof ResourceType];

export const ResourceStatus = {
  AVAILABLE: 'AVAILABLE',
  RESERVED: 'RESERVED',
  IN_USE: 'IN_USE',
  PAUSED: 'PAUSED',
  MAINTENANCE: 'MAINTENANCE',
  OUT_OF_SERVICE: 'OUT_OF_SERVICE',
} as const;
export type ResourceStatus = (typeof ResourceStatus)[keyof typeof ResourceStatus];

export const ResourceSessionStatus = {
  SCHEDULED: 'SCHEDULED',
  ACTIVE: 'ACTIVE',
  PAUSED: 'PAUSED',
  ENDED: 'ENDED',
  CANCELLED: 'CANCELLED',
} as const;
export type ResourceSessionStatus =
  (typeof ResourceSessionStatus)[keyof typeof ResourceSessionStatus];

export const FulfillmentType = {
  DINE_IN: 'DINE_IN',
  TAKEAWAY: 'TAKEAWAY',
  DELIVERY: 'DELIVERY',
} as const;
export type FulfillmentType = (typeof FulfillmentType)[keyof typeof FulfillmentType];

export const OrderStatus = {
  DRAFT: 'DRAFT',
  CONFIRMED: 'CONFIRMED',
  SENT_TO_KITCHEN: 'SENT_TO_KITCHEN',
  PREPARING: 'PREPARING',
  READY: 'READY',
  SERVED: 'SERVED',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
  VOIDED: 'VOIDED',
  REFUNDED: 'REFUNDED',
} as const;
export type OrderStatus = (typeof OrderStatus)[keyof typeof OrderStatus];

/** §15 — what the guest sees; a projection of the internal states above. */
export const CustomerOrderStatus = {
  ORDER_RECEIVED: 'ORDER_RECEIVED',
  CONFIRMED: 'CONFIRMED',
  PREPARING: 'PREPARING',
  PARTIALLY_READY: 'PARTIALLY_READY',
  READY: 'READY',
  SERVED: 'SERVED',
  PICKED_UP: 'PICKED_UP',
  COMPLETED: 'COMPLETED',
} as const;
export type CustomerOrderStatus =
  (typeof CustomerOrderStatus)[keyof typeof CustomerOrderStatus];

export const TakeawayStatus = {
  DRAFT: 'DRAFT',
  SCHEDULED: 'SCHEDULED',
  RELEASED: 'RELEASED',
  PREPARING: 'PREPARING',
  READY: 'READY',
  HANDED_OVER: 'HANDED_OVER',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
  REFUNDED: 'REFUNDED',
} as const;
export type TakeawayStatus = (typeof TakeawayStatus)[keyof typeof TakeawayStatus];

export const ReleaseStrategy = {
  IMMEDIATE: 'IMMEDIATE',
  SCHEDULED: 'SCHEDULED',
  ON_CUSTOMER_REQUEST: 'ON_CUSTOMER_REQUEST',
} as const;
export type ReleaseStrategy = (typeof ReleaseStrategy)[keyof typeof ReleaseStrategy];

export const OrderItemStatus = {
  PENDING: 'PENDING',
  QUEUED: 'QUEUED',
  PREPARING: 'PREPARING',
  READY: 'READY',
  AWAITING_PICKUP: 'AWAITING_PICKUP',
  PICKED_UP: 'PICKED_UP',
  SERVED: 'SERVED',
  CANCELLED: 'CANCELLED',
} as const;
export type OrderItemStatus = (typeof OrderItemStatus)[keyof typeof OrderItemStatus];

export const CheckStatus = {
  OPEN: 'OPEN',
  PARTIALLY_PAID: 'PARTIALLY_PAID',
  PAID: 'PAID',
  CLOSED: 'CLOSED',
  VOIDED: 'VOIDED',
  REFUNDED: 'REFUNDED',
} as const;
export type CheckStatus = (typeof CheckStatus)[keyof typeof CheckStatus];

export const PaymentStatus = {
  PENDING: 'PENDING',
  AUTHORIZED: 'AUTHORIZED',
  PAID: 'PAID',
  FAILED: 'FAILED',
  PARTIALLY_REFUNDED: 'PARTIALLY_REFUNDED',
  REFUNDED: 'REFUNDED',
} as const;
export type PaymentStatus = (typeof PaymentStatus)[keyof typeof PaymentStatus];

export const PaymentMethod = {
  CASH: 'CASH',
  CARD: 'CARD',
  WALLET: 'WALLET',
  BANK_TRANSFER: 'BANK_TRANSFER',
  VOUCHER: 'VOUCHER',
  LOYALTY_POINTS: 'LOYALTY_POINTS',
  OTHER: 'OTHER',
} as const;
export type PaymentMethod = (typeof PaymentMethod)[keyof typeof PaymentMethod];

export const ChargeType = {
  PRODUCT: 'PRODUCT',
  MEAL: 'MEAL',
  SERVICE: 'SERVICE',
  TIME: 'TIME',
  FEE: 'FEE',
  DISCOUNT: 'DISCOUNT',
  ADJUSTMENT: 'ADJUSTMENT',
  TIP: 'TIP',
  TAX: 'TAX',
} as const;
export type ChargeType = (typeof ChargeType)[keyof typeof ChargeType];

export const ServiceRequestType = {
  CALL_WAITER: 'CALL_WAITER',
  REQUEST_BILL: 'REQUEST_BILL',
  WATER: 'WATER',
  NAPKINS: 'NAPKINS',
  PLATES: 'PLATES',
  CLEANING: 'CLEANING',
  CUSTOM: 'CUSTOM',
} as const;
export type ServiceRequestType = (typeof ServiceRequestType)[keyof typeof ServiceRequestType];

export const ServiceRequestStatus = {
  CREATED: 'CREATED',
  ACCEPTED: 'ACCEPTED',
  DONE: 'DONE',
  CANCELLED: 'CANCELLED',
  ESCALATED: 'ESCALATED',
} as const;
export type ServiceRequestStatus =
  (typeof ServiceRequestStatus)[keyof typeof ServiceRequestStatus];

export const ScopeLevel = {
  PLATFORM: 'PLATFORM',
  TENANT: 'TENANT',
  BRANCH: 'BRANCH',
  FLOOR: 'FLOOR',
  AREA: 'AREA',
  TABLE: 'TABLE',
  RESOURCE: 'RESOURCE',
} as const;
export type ScopeLevel = (typeof ScopeLevel)[keyof typeof ScopeLevel];

export const CatalogItemType = {
  PRODUCT: 'PRODUCT',
  MEAL_COMBO: 'MEAL_COMBO',
  SERVICE: 'SERVICE',
  TIME_BASED_SERVICE: 'TIME_BASED_SERVICE',
  EXPERIENCE: 'EXPERIENCE',
  PACKAGE: 'PACKAGE',
  ADD_ON: 'ADD_ON',
} as const;
export type CatalogItemType = (typeof CatalogItemType)[keyof typeof CatalogItemType];

export const SpendingPolicy = {
  UNLIMITED: 'UNLIMITED',
  LIMIT_AMOUNT: 'LIMIT_AMOUNT',
  APPROVE_ALL: 'APPROVE_ALL',
  APPROVE_ABOVE_THRESHOLD: 'APPROVE_ABOVE_THRESHOLD',
  NO_PURCHASES: 'NO_PURCHASES',
} as const;
export type SpendingPolicy = (typeof SpendingPolicy)[keyof typeof SpendingPolicy];

export const SubscriptionStatus = {
  TRIAL: 'TRIAL',
  ACTIVE: 'ACTIVE',
  PAST_DUE: 'PAST_DUE',
  CANCELLED: 'CANCELLED',
  EXPIRED: 'EXPIRED',
} as const;
export type SubscriptionStatus = (typeof SubscriptionStatus)[keyof typeof SubscriptionStatus];

export const ReservationStatus = {
  PENDING: 'PENDING',
  CONFIRMED: 'CONFIRMED',
  SEATED: 'SEATED',
  NO_SHOW: 'NO_SHOW',
  CANCELLED: 'CANCELLED',
  COMPLETED: 'COMPLETED',
} as const;
export type ReservationStatus = (typeof ReservationStatus)[keyof typeof ReservationStatus];

export const PurchaseOrderStatus = {
  DRAFT: 'DRAFT',
  SUBMITTED: 'SUBMITTED',
  APPROVED: 'APPROVED',
  PARTIALLY_RECEIVED: 'PARTIALLY_RECEIVED',
  RECEIVED: 'RECEIVED',
  CANCELLED: 'CANCELLED',
  CLOSED: 'CLOSED',
} as const;
export type PurchaseOrderStatus = (typeof PurchaseOrderStatus)[keyof typeof PurchaseOrderStatus];

/* ------------------------------------------------------------------ *
 * X-12 — enums that lived only in `schema.prisma` while a client
 * hand-mirrored them. Every one below replaced a local union or
 * `as const` array in dashboard / platform-admin / a mobile app, so the
 * names now have exactly one definition and cannot drift silently.
 * ------------------------------------------------------------------ */

export const PartyStatus = {
  OPEN: 'OPEN',
  CLOSED: 'CLOSED',
  CANCELLED: 'CANCELLED',
} as const;
export type PartyStatus = (typeof PartyStatus)[keyof typeof PartyStatus];

export const SessionStatus = {
  OPEN: 'OPEN',
  CLOSED: 'CLOSED',
  CANCELLED: 'CANCELLED',
} as const;
export type SessionStatus = (typeof SessionStatus)[keyof typeof SessionStatus];

export const PricingUnit = {
  PER_MINUTE: 'PER_MINUTE',
  PER_30_MINUTES: 'PER_30_MINUTES',
  PER_HOUR: 'PER_HOUR',
  FIXED_DURATION: 'FIXED_DURATION',
  FIXED_PRICE: 'FIXED_PRICE',
} as const;
export type PricingUnit = (typeof PricingUnit)[keyof typeof PricingUnit];

/** The five §24.5 policy gates. */
export const PolicyKind = {
  DISCOUNT: 'DISCOUNT',
  VOID: 'VOID',
  REFUND: 'REFUND',
  PRICE_OVERRIDE: 'PRICE_OVERRIDE',
  STOCK_ADJUSTMENT: 'STOCK_ADJUSTMENT',
} as const;
export type PolicyKind = (typeof PolicyKind)[keyof typeof PolicyKind];

export const ApprovalStatus = {
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  EXPIRED: 'EXPIRED',
} as const;
export type ApprovalStatus = (typeof ApprovalStatus)[keyof typeof ApprovalStatus];

export const KitchenTicketStatus = {
  NEW: 'NEW',
  PREPARING: 'PREPARING',
  READY: 'READY',
  /**
   * The guest has it. Every non-cancelled line reached SERVED or PICKED_UP, so
   * the pass is finished with the ticket and the board stops showing it. READY
   * only ever meant "on the pass", which is why an uncollected takeaway bag sat
   * in the pickup queue for days.
   *
   * Placed after READY on purpose: `enums.drift.spec.ts` in the backend
   * compares member ORDER against the Prisma enum, not just membership.
   */
  COLLECTED: 'COLLECTED',
  RECALLED: 'RECALLED',
  CANCELLED: 'CANCELLED',
} as const;
export type KitchenTicketStatus = (typeof KitchenTicketStatus)[keyof typeof KitchenTicketStatus];

export const DeviceType = {
  POS: 'POS',
  KDS: 'KDS',
  WAITER_TABLET: 'WAITER_TABLET',
  CUSTOMER_KIOSK: 'CUSTOMER_KIOSK',
  PRINTER: 'PRINTER',
  OTHER: 'OTHER',
} as const;
export type DeviceType = (typeof DeviceType)[keyof typeof DeviceType];

export const EntitlementType = {
  CATALOG_ITEM: 'CATALOG_ITEM',
  RESOURCE_TIME: 'RESOURCE_TIME',
} as const;
export type EntitlementType = (typeof EntitlementType)[keyof typeof EntitlementType];

export const EntitlementStatus = {
  TEMPLATE: 'TEMPLATE',
  ISSUED: 'ISSUED',
  PARTIALLY_CONSUMED: 'PARTIALLY_CONSUMED',
  CONSUMED: 'CONSUMED',
  EXPIRED: 'EXPIRED',
  CANCELLED: 'CANCELLED',
} as const;
export type EntitlementStatus = (typeof EntitlementStatus)[keyof typeof EntitlementStatus];

export const UnitType = {
  WEIGHT: 'WEIGHT',
  VOLUME: 'VOLUME',
  COUNT: 'COUNT',
  LENGTH: 'LENGTH',
  TIME: 'TIME',
} as const;
export type UnitType = (typeof UnitType)[keyof typeof UnitType];

export const InventoryItemType = {
  RAW_MATERIAL: 'RAW_MATERIAL',
  FINISHED_GOOD: 'FINISHED_GOOD',
  PACKAGING: 'PACKAGING',
  CONSUMABLE: 'CONSUMABLE',
} as const;
export type InventoryItemType = (typeof InventoryItemType)[keyof typeof InventoryItemType];

export const StockMovementType = {
  PURCHASE_RECEIPT: 'PURCHASE_RECEIPT',
  SALE_CONSUMPTION: 'SALE_CONSUMPTION',
  WASTE: 'WASTE',
  TRANSFER_IN: 'TRANSFER_IN',
  TRANSFER_OUT: 'TRANSFER_OUT',
  ADJUSTMENT: 'ADJUSTMENT',
  STOCK_COUNT: 'STOCK_COUNT',
  SUPPLIER_RETURN: 'SUPPLIER_RETURN',
  PRODUCTION: 'PRODUCTION',
} as const;
export type StockMovementType = (typeof StockMovementType)[keyof typeof StockMovementType];

export const RecipeVersionStatus = {
  DRAFT: 'DRAFT',
  ACTIVE: 'ACTIVE',
  ARCHIVED: 'ARCHIVED',
} as const;
export type RecipeVersionStatus = (typeof RecipeVersionStatus)[keyof typeof RecipeVersionStatus];

export const PurchaseReceiptStatus = {
  DRAFT: 'DRAFT',
  RECEIVED: 'RECEIVED',
  PARTIALLY_RETURNED: 'PARTIALLY_RETURNED',
  RETURNED: 'RETURNED',
  CANCELLED: 'CANCELLED',
} as const;
export type PurchaseReceiptStatus =
  (typeof PurchaseReceiptStatus)[keyof typeof PurchaseReceiptStatus];

export const FeedbackStatus = {
  NEW: 'NEW',
  ACKNOWLEDGED: 'ACKNOWLEDGED',
  IN_RECOVERY: 'IN_RECOVERY',
  RESOLVED: 'RESOLVED',
  DISMISSED: 'DISMISSED',
} as const;
export type FeedbackStatus = (typeof FeedbackStatus)[keyof typeof FeedbackStatus];

export const PromotionType = {
  PERCENTAGE: 'PERCENTAGE',
  FIXED_AMOUNT: 'FIXED_AMOUNT',
  BUY_X_GET_Y: 'BUY_X_GET_Y',
  BUNDLE_PRICE: 'BUNDLE_PRICE',
  FREE_ITEM: 'FREE_ITEM',
  HAPPY_HOUR: 'HAPPY_HOUR',
} as const;
export type PromotionType = (typeof PromotionType)[keyof typeof PromotionType];

export const CouponStatus = {
  ACTIVE: 'ACTIVE',
  REDEEMED: 'REDEEMED',
  EXPIRED: 'EXPIRED',
  DISABLED: 'DISABLED',
} as const;
export type CouponStatus = (typeof CouponStatus)[keyof typeof CouponStatus];
